import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { LEVEL_CONFIG } from '../../../config/level.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { deckLineHeight, type DeckProfile } from '../deckLines';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  laneCenterX,
  moduleSides,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import {
  SegmentKind,
  type CollectibleSpawnPoint,
  type FixedObstaclePlacement,
  type GroundSurface,
  type SegmentDefinition,
  type SegmentGenerationRules,
  type SegmentVisualPart,
} from '../SegmentDefinition';
import { CONTAINER_20FT, NARROW_OBSTACLES } from './SEG_CONTAINERS_40M';

/**
 * SEG_CONTAINER_TOWER_60M – "Containerturm" (user 2026-10-03: "drei, vier Container gestapelt … zwei, dreimal mit
 * Doppelsprüngen hoch … ganz oben ist dann eine Bierkiste … das kann ruhig öfter vorkommen, muss aber nicht immer
 * zwangsweise eine Kiste oben stehen"). A staircase of 20 ft containers in lane 0:
 *
 *   step 1   three containers end to end on the road, roof 2.59 m (z 14 – 32.78) – double jump up from the road
 *   step 2   a column of two stacked containers right behind (z 32.78 – 38.84), roof 5.18 m – double jump up from
 *            the step-1 roof (the same 2.59 m rise). The 18.8 m of step-1 roof are its run-up: a fair double jump at
 *            top speed takes off ≈ 0.46 s before the face with ≥ 0.25 s of timing (terrainAnalysis, tests).
 *   top      a beer crate (turbo) up there in half of the placements (FixedCollectiblePlacement.chance)
 *   drop     off the column's end: 5.18 m down, lane 0 stays free to the module end; sideways off a roof is a drop too
 *   ground   lanes 1–2 with rows like SEG_CONTAINERS_40M – narrow obstacles next to the tower, never a lane change
 *            into it (walls along the stack).
 *
 * Collider = terrain: one surface per step box (the column is one box from the road to 5.18 m).
 */
export const CONTAINER_TOWER = {
  length: 60,
  lane: 0,
  /** Face of step 1 – the run-up before it is the module's own flat road (as SEG_CONTAINERS_40M). */
  zFrom: 14,
  stepOneCount: 3,
  gap: 0.3,
  edgeClearance: 0.05,
  /** Containers stacked in the column of step 2. */
  columnHeight: 2,
  /** Chance of the beer crate on top. */
  crateChance: 0.5,
  rows: { R06: 6, R20: 20, R26: 26, R40: 40, R47: 47, R54: 54 },
  collectibleSpacing: 2,
} as const;

export interface TowerBox {
  readonly id: string;
  readonly zFrom: number;
  readonly zTo: number;
  readonly xFrom: number;
  readonly xTo: number;
  /** Containers stacked here (1 = on the road). */
  readonly levels: number;
}

/** The staircase with step 1 starting at zFrom (SEG_CONTAINER_TOWER_SHELL_60M puts it against its end). */
export function towerBoxes(zFrom: number = CONTAINER_TOWER.zFrom): TowerBox[] {
  const { length, width } = CONTAINER_20FT;
  const edge = WORLD_CONFIG.trackWidth / 2 - CONTAINER_TOWER.edgeClearance;
  const outside = Math.sign(laneCenterX(WORLD_CONFIG, CONTAINER_TOWER.lane)) < 0;
  const xFrom = outside ? -edge : edge - width;
  const boxes: TowerBox[] = [];
  let z = zFrom;
  for (let i = 0; i < CONTAINER_TOWER.stepOneCount; i++) {
    boxes.push({ id: `S1_${i + 1}`, zFrom: z, zTo: z + length, xFrom, xTo: xFrom + width, levels: 1 });
    // end to end without a gap: one flat roof – the terrain analysis counts the run-up for step 2 from the last gap
    z += length;
  }
  // the column stands right against the last step-1 container: its face rises straight from the roof
  boxes.push({ id: 'S2', zFrom: z, zTo: z + length, xFrom, xTo: xFrom + width, levels: CONTAINER_TOWER.columnHeight });
  return boxes;
}

export const TOWER_BOXES: readonly TowerBox[] = towerBoxes();
const STEP_ONE_END = TOWER_BOXES[CONTAINER_TOWER.stepOneCount - 1]!.zTo;
const COLUMN = TOWER_BOXES[TOWER_BOXES.length - 1]!;
export const TOWER_TOP = CONTAINER_20FT.height * CONTAINER_TOWER.columnHeight;

/** One terrain surface per box (the column is one box from the road up). */
export const towerSurfaces = (boxes: readonly TowerBox[]): GroundSurface[] => boxes.map((box) => ({
  id: `SURF_CONTAINER_${box.id}`,
  zFrom: box.zFrom,
  zTo: box.zTo,
  xFrom: box.xFrom,
  xTo: box.xTo,
  heightFrom: CONTAINER_20FT.height * box.levels,
  heightTo: CONTAINER_20FT.height * box.levels,
}));
const surfaces = towerSurfaces(TOWER_BOXES);

/** The container visuals of the boxes, stacked ones on the roof below. */
export const towerVisuals = (boxes: readonly TowerBox[]): SegmentVisualPart[] => boxes.flatMap((box) =>
  Array.from({ length: box.levels }, (_, level) => ({
    assetId: CONTAINER_20FT.assetId,
    // asset origin = ground centre of the body; stacked ones stand on the roof below
    position: { x: (box.xFrom + box.xTo) / 2, y: WORLD_CONFIG.groundHeight + level * CONTAINER_20FT.height, z: (box.zFrom + box.zTo) / 2 },
    yaw: CONTAINER_20FT.yawDoorsToRunner,
  })),
);
const containerVisuals = towerVisuals(TOWER_BOXES);

const GENERATION: SegmentGenerationRules = {
  // own family: it must not take the slots of the container row (SEG_CONTAINERS_40M keeps its ≈ 0.75 per km)
  // user 2026-10-03: should come more often and early (was 0.12 from 800 m)
  weight: 0.16,  // was 0.3 – from 2026-10-05 the special modules are scaled up by the tier (1.2 … 2.8 ×): the crate on top stays rare
  minDistance: 350,
  cooldownDistance: 400,
  minSegmentsAfterJunction: 1,
  family: 'CONTAINER_TOWER',
  tags: ['containers', 'terrain', 'platform', 'double-jump', 'tower'],
};

// ── Obstacles (ground route, lanes 1–2) ─────────────────────────────────────

const [L0, L1, L2] = [CONTAINER_TOWER.lane, 1, WORLD_CONFIG.laneCount - 1] as const;
const OPEN_TYPES = [ObstacleType.Jump, ObstacleType.Slide] as const;
const NARROW_TYPES = [ObstacleType.Jump, ObstacleType.DoubleJump] as const;
const { R06, R20, R26, R40, R47, R54 } = CONTAINER_TOWER.rows;
const spawnPoints = [
  ...obstacleRowOn(WORLD_CONFIG, 'R06', R06, [L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R20', R20, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R20', R20, [L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R26', R26, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R26', R26, [L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R40', R40, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R40', R40, [L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R47', R47, [L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R54', R54, [L1, L2], OPEN_TYPES),
];
const at = (row: string, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });
const [N1, N2, N3, N4] = NARROW_OBSTACLES as readonly [string, string, string, string];

// ── Bottles and the crate ────────────────────────────────────────────────────

const LINE_SPEED = new DifficultyManager(DIFFICULTY_CONFIG).speedAt(GENERATION.minDistance);
const STEP_ONE: DeckProfile = { face: CONTAINER_TOWER.zFrom, end: STEP_ONE_END, top: CONTAINER_20FT.height };
const SPACING = CONTAINER_TOWER.collectibleSpacing;
const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
/** The roof line ends where the second double jump starts (the climb onto the column needs the roof free of bottles to aim). */
const ROOF_LINE_END = COLUMN.zFrom - 9;

const CRATE_POINT: CollectibleSpawnPoint = {
  id: 'SOCKET_COL_TOWER_TOP',
  lane: L0,
  position: { x: laneCenterX(WORLD_CONFIG, L0), y: TOWER_TOP + COLLECTIBLE_FLOAT_HEIGHT, z: (COLUMN.zFrom + COLUMN.zTo) / 2 },
};

const collectibles: CollectibleSpawnPoint[] = [
  ...collectibleLine(WORLD_CONFIG, 'ROOF', [{ lane: L0, from: 1, to: ROOF_LINE_END, spacing: SPACING, height: deckLineHeight(STEP_ONE, LINE_SPEED) }]),
  ...collectibleLine(WORLD_CONFIG, 'GROUND_L2', [{ lane: L2, from: 1, to: CONTAINER_TOWER.length - 1, spacing: SPACING, height: flat }]),
  CRATE_POINT,
];

export const SEG_CONTAINER_TOWER_60M: SegmentDefinition = {
  id: 'SEG_CONTAINER_TOWER_60M',
  kind: SegmentKind.Straight,
  displayName: 'Containerturm 60 m',
  length: CONTAINER_TOWER.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 40 } },
    ...containerVisuals,
  ],

  entry: entryConnector(),
  exits: [straightExit(CONTAINER_TOWER.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: { surfaces, gaps: [], ceilings: [] },

  obstacleSpawnPoints: spawnPoints,
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    {
      id: 'GROUND_RIGHT_FIRST',
      weight: 1,
      placements: [at('R06', L2, 'OBS_BARRIER_A'), at('R20', L1, N3), at('R26', L2, 'OBS_SCAFFOLD_PASS_A'), at('R40', L1, N1), at('R47', L2, 'OBS_CONE_ROW_A'), at('R54', L1, 'OBS_BARRIER_BOARD_A')],
    },
    {
      id: 'GROUND_MIDDLE_FIRST',
      weight: 1,
      placements: [at('R06', L1, 'OBS_PALLET_A'), at('R20', L2, 'OBS_SCAFFOLD_PASS_A'), at('R26', L1, N4), at('R40', L2, 'OBS_BARRIER_A'), at('R47', L1, 'OBS_CONE_ROW_A'), at('R54', L2, 'OBS_PALLET_A')],
    },
    {
      id: 'GROUND_BOTH',
      weight: 1,
      placements: [
        at('R06', L1, 'OBS_CONE_ROW_A'),
        at('R06', L2, 'OBS_SCAFFOLD_PASS_A'),
        at('R20', L1, N2),
        at('R20', L2, 'OBS_BARRIER_A'),
        at('R40', L1, N1),
        at('R40', L2, 'OBS_SCAFFOLD_PASS_A'),
        at('R47', L1, 'OBS_BARRIER_BOARD_A'),
        at('R54', L2, 'OBS_PALLET_A'),
      ],
    },
  ],

  collectibleSpawnPoints: collectibles,
  // the crate like every turbo crate only from LevelConfig.turboCrates.fromDistance on – the tower itself comes earlier
  fixedCollectibles: [{ spawnPointId: CRATE_POINT.id, collectibleId: 'COL_BEER_CRATE_A', chance: CONTAINER_TOWER.crateChance, minDistance: LEVEL_CONFIG.turboCrates.fromDistance }],

  ...moduleSides(CONTAINER_TOWER.length),

  generation: GENERATION,
};
